import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Spin, Tag } from "antd";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MeasuringStrategy,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDispatch, useSelector } from "react-redux";

import BacklogList from "../components/BacklogList";
import CompleteSprintModal from "../components/CompleteSprintModal";
import CreateSprintInline from "../components/CreateSprintInline";
import IssueFormModal from "../../issues/components/IssueFormModal";
import SprintRow from "../components/SprintRow";
import {
  completeSprint,
  createIssue,
  createSprint,
  deleteSprint,
  fetchIssues,
  fetchSprints,
  moveIssue,
  reorderSprints,
  startSprint,
  updateSprint,
} from "../../../../core/data/redux/projects/thunks";
import {
  selectBoards,
  selectCurrentProject,
  selectIssuesByProject,
  selectIssuesLoading,
  selectSprints,
  selectSprintsLoading,
  selectStatuses,
} from "../../../../core/data/redux/projects/selectors";
import { useProjectCapability } from "../../hooks/useProjectRole";
import { errMsg } from "../../helpers/errMsg.helper";
import { CONFIRM_MODAL_PROPS } from "../../helpers/modal.style";
import { notify } from "../../../../services/toast.config";

import type {
  Issue,
  Sprint,
} from "../../../../core/data/redux/projects/types";
import type { AppDispatch } from "../../../../core/data/redux/store";

const BacklogPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const project = useSelector(selectCurrentProject);
  const boards = useSelector(selectBoards);
  const statuses = useSelector(selectStatuses);
  const sprints = useSelector(selectSprints);
  const sprintsLoading = useSelector(selectSprintsLoading);
  const projectIssues = useSelector(
    selectIssuesByProject(project?.id ?? null)
  );
  const issuesLoading = useSelector(selectIssuesLoading);

  const canManageSprint = useProjectCapability(project?.id, "MANAGE_SPRINT");
  const canDeleteSprint = useProjectCapability(project?.id, "DELETE_SPRINT");
  const canCreateIssue = useProjectCapability(project?.id, "CREATE_ISSUE");

  const defaultBoard = useMemo(
    () => boards.find((b) => b.is_default) || boards[0] || null,
    [boards]
  );

  const [createSprintOpen, setCreateSprintOpen] = useState(false);
  const [submittingSprint, setSubmittingSprint] = useState(false);
  const [completeSprintTarget, setCompleteSprintTarget] = useState<Sprint | null>(
    null
  );
  const [completing, setCompleting] = useState(false);
  const [createIssueOpen, setCreateIssueOpen] = useState(false);
  const [submittingIssue, setSubmittingIssue] = useState(false);
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
  const [optimisticIssues, setOptimisticIssues] = useState<Issue[] | null>(null);
  const [optimisticSprintIds, setOptimisticSprintIds] = useState<number[] | null>(
    null
  );
  const [activeDrag, setActiveDrag] = useState<
    | { kind: "ISSUE"; issue: Issue }
    | { kind: "SPRINT"; sprint: Sprint }
    | null
  >(null);

  useEffect(() => {
    if (project?.id) {
      dispatch(fetchSprints({ projectId: project.id }));
      dispatch(fetchIssues({ project_id: project.id }));
    }
  }, [dispatch, project?.id]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) return pointerCollisions;
    return rectIntersection(args);
  }, []);

  const issuesToShow = optimisticIssues || projectIssues || [];

  const visibleSprints = useMemo(() => {
    const filtered = sprints
      .filter((s) => s.state !== "COMPLETED")
      .sort((a, b) => {
        if (a.state === b.state) return a.position - b.position;
        if (a.state === "ACTIVE") return -1;
        if (b.state === "ACTIVE") return 1;
        return 0;
      });
    if (!optimisticSprintIds) return filtered;
    const map = new Map(filtered.map((s) => [Number(s.id), s]));
    return optimisticSprintIds
      .map((id) => map.get(Number(id)))
      .filter((s): s is Sprint => Boolean(s));
  }, [sprints, optimisticSprintIds]);

  const issuesBySprint = useMemo(() => {
    const map = new Map<number | "BACKLOG", Issue[]>();
    map.set("BACKLOG", []);
    visibleSprints.forEach((s) => map.set(Number(s.id), []));
    for (const issue of issuesToShow) {
      if (!issue.is_active) continue;
      const key: number | "BACKLOG" =
        issue.sprint_id == null ? "BACKLOG" : Number(issue.sprint_id);
      if (!map.has(key)) map.set(key, []);
      const list = map.get(key);
      if (list) list.push(issue);
    }
    map.forEach((list) =>
      list.sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0))
    );
    return map;
  }, [issuesToShow, visibleSprints]);

  const findIssueByDragId = (dragId: string): Issue | undefined => {
    const numId = Number(dragId.replace("issue-", ""));
    return issuesToShow.find((i) => Number(i.id) === numId);
  };

  const findContainerForDrop = (
    overId: string,
    dropData: any
  ): { kind: "BACKLOG" } | { kind: "SPRINT"; sprintId: number } | null => {
    if (overId === "backlog-target" || dropData?.backlogTarget) {
      return { kind: "BACKLOG" };
    }
    if (
      overId.startsWith("sprint-target-") ||
      dropData?.sprintTarget ||
      overId.startsWith("sprint-")
    ) {
      const sprintId = Number(
        overId.replace("sprint-target-", "").replace("sprint-", "")
      );
      if (!isNaN(sprintId)) return { kind: "SPRINT", sprintId };
    }
    if (overId.startsWith("issue-")) {
      const target = findIssueByDragId(overId);
      if (!target) return null;
      return target.sprint_id == null
        ? { kind: "BACKLOG" }
        : { kind: "SPRINT", sprintId: Number(target.sprint_id) };
    }
    return null;
  };

  const handleSprintReorderEnd = async (
    activeId: number,
    overId: number
  ) => {
    if (activeId === overId) return;
    const planned = visibleSprints.filter((s) => s.state === "PLANNED");
    const oldIndex = planned.findIndex((s) => s.id === activeId);
    const newIndex = planned.findIndex((s) => s.id === overId);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(planned, oldIndex, newIndex);
    const fullOrder = visibleSprints.map((s) => {
      if (s.state === "PLANNED") {
        return reordered.shift() || s;
      }
      return s;
    });
    setOptimisticSprintIds(fullOrder.map((s) => s.id));
    try {
      await dispatch(
        reorderSprints(
          fullOrder.filter((s) => s.state === "PLANNED").map((s) => s.id)
        )
      ).unwrap();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to reorder sprints"));
    } finally {
      setOptimisticSprintIds(null);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    if (id.startsWith("issue-")) {
      const numId = Number(id.replace("issue-", ""));
      const found = issuesToShow.find((i) => Number(i.id) === numId);
      if (found) setActiveDrag({ kind: "ISSUE", issue: found });
    } else if (id.startsWith("sprint-")) {
      const numId = Number(id.replace("sprint-", ""));
      const found = visibleSprints.find((s) => Number(s.id) === numId);
      if (found) setActiveDrag({ kind: "SPRINT", sprint: found });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    if (activeIdStr.startsWith("sprint-")) {
      const activeNum = Number(activeIdStr.replace("sprint-", ""));
      const overNum = Number(
        overIdStr.replace("sprint-target-", "").replace("sprint-", "")
      );
      if (!isNaN(activeNum) && !isNaN(overNum)) {
        await handleSprintReorderEnd(activeNum, overNum);
      }
      return;
    }

    if (!activeIdStr.startsWith("issue-")) return;

    const dragged = findIssueByDragId(activeIdStr);
    if (!dragged) return;

    const target = findContainerForDrop(overIdStr, over.data?.current);
    if (!target) return;

    const newSprintId =
      target.kind === "BACKLOG" ? null : target.sprintId;

    const targetIssues =
      target.kind === "BACKLOG"
        ? issuesBySprint.get("BACKLOG") || []
        : issuesBySprint.get(Number(target.sprintId)) || [];

    let beforeId: number | null = null;
    let afterId: number | null = null;

    if (overIdStr.startsWith("issue-")) {
      const overIssue = findIssueByDragId(overIdStr);
      if (overIssue && Number(overIssue.id) !== Number(dragged.id)) {
        const idxInTarget = targetIssues.findIndex(
          (i) => Number(i.id) === Number(overIssue.id)
        );
        if (idxInTarget >= 0) {
          beforeId =
            idxInTarget > 0 && Number(targetIssues[idxInTarget - 1].id) !== Number(dragged.id)
              ? Number(targetIssues[idxInTarget - 1].id)
              : null;
          afterId = Number(overIssue.id);
        }
      }
    } else if (targetIssues.length > 0) {
      beforeId = Number(targetIssues[targetIssues.length - 1].id);
    }

    if (
      (dragged.sprint_id == null && newSprintId == null) ||
      Number(dragged.sprint_id) === newSprintId
    ) {
      if (beforeId == null && afterId == null) return;
    }

    const optimistic = issuesToShow.map((i) =>
      Number(i.id) === Number(dragged.id) ? { ...i, sprint_id: newSprintId } : i
    );
    setOptimisticIssues(optimistic);

    try {
      await dispatch(
        moveIssue({
          id: Number(dragged.id),
          sprint_id: newSprintId,
          before_id: beforeId ? Number(beforeId) : null,
          after_id: afterId ? Number(afterId) : null,
        })
      ).unwrap();
    } catch (e: any) {
      notify.error(errMsg(e, "Failed to move issue"));
    } finally {
      setOptimisticIssues(null);
    }
  };

  const handleCreateSprint = useCallback(
    async (payload: {
      name: string;
      goal: string | null;
      start_date: string | null;
      end_date: string | null;
    }) => {
      if (!project || !defaultBoard) return;
      setSubmittingSprint(true);
      try {
        await dispatch(
          createSprint({
            project_id: project.id,
            board_id: defaultBoard.id,
            ...payload,
          })
        ).unwrap();
        notify.success("Sprint created");
        setCreateSprintOpen(false);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to create sprint"));
        throw e;
      } finally {
        setSubmittingSprint(false);
      }
    },
    [dispatch, project, defaultBoard]
  );

  const handleStartSprint = useCallback(
    async (sprint: Sprint) => {
      Modal.confirm({
        title: `Start sprint '${sprint.name}'?`,
        content:
          "All issues in this sprint will appear on the active sprint board.",
        okText: "Start",
        ...CONFIRM_MODAL_PROPS,
        onOk: async () => {
          try {
            await dispatch(startSprint(sprint.id)).unwrap();
            notify.success("Sprint started");
          } catch (e: any) {
            notify.error(errMsg(e, "Failed to start sprint"));
          }
        },
      });
    },
    [dispatch]
  );

  const handleCompleteSprintClick = useCallback((sprint: Sprint) => {
    setCompleteSprintTarget(sprint);
  }, []);

  const handleCompleteSprintSubmit = useCallback(
    async (target: "BACKLOG" | number) => {
      if (!completeSprintTarget) return;
      setCompleting(true);
      try {
        await dispatch(
          completeSprint({
            sprintId: completeSprintTarget.id,
            moveUnfinishedTo: target,
          })
        ).unwrap();
        if (project?.id) {
          dispatch(fetchSprints({ projectId: project.id }));
          dispatch(fetchIssues({ project_id: project.id }));
        }
        notify.success("Sprint completed");
        setCompleteSprintTarget(null);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to complete sprint"));
      } finally {
        setCompleting(false);
      }
    },
    [dispatch, completeSprintTarget, project?.id]
  );

  const handleEditSprintSubmit = useCallback(
    async (payload: {
      name: string;
      goal: string | null;
      start_date: string | null;
      end_date: string | null;
    }) => {
      if (!editingSprint) return;
      setSubmittingSprint(true);
      try {
        await dispatch(
          updateSprint({ sprintId: editingSprint.id, payload })
        ).unwrap();
        notify.success("Sprint updated");
        setEditingSprint(null);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to update sprint"));
        throw e;
      } finally {
        setSubmittingSprint(false);
      }
    },
    [dispatch, editingSprint]
  );

  const handleDeleteSprint = useCallback(
    (sprint: Sprint) => {
      Modal.confirm({
        title: `Delete sprint '${sprint.name}'?`,
        content:
          "Issues currently in this sprint will be moved to the backlog.",
        okText: "Delete",
        okButtonProps: { danger: true },
        ...CONFIRM_MODAL_PROPS,
        onOk: async () => {
          try {
            await dispatch(deleteSprint(sprint.id)).unwrap();
            if (project?.id) {
              dispatch(fetchIssues({ project_id: project.id }));
            }
            notify.success("Sprint deleted");
          } catch (e: any) {
            notify.error(errMsg(e, "Failed to delete sprint"));
          }
        },
      });
    },
    [dispatch, project?.id]
  );

  const handleCreateIssue = useCallback(
    async (payload: Record<string, any>) => {
      setSubmittingIssue(true);
      try {
        await dispatch(createIssue(payload)).unwrap();
        if (project?.id) {
          dispatch(fetchIssues({ project_id: project.id }));
        }
        notify.success("Issue created");
        setCreateIssueOpen(false);
      } catch (e: any) {
        notify.error(errMsg(e, "Failed to create issue"));
        throw e;
      } finally {
        setSubmittingIssue(false);
      }
    },
    [dispatch, project?.id]
  );

  if (!project) {
    return (
      <div className="card">
        <div className="card-body text-center py-4">
          <Spin />
        </div>
      </div>
    );
  }

  const plannedSprints = sprints.filter((s) => s.state === "PLANNED");
  const sprintRowIds = visibleSprints.map((s) => `sprint-${s.id}`);

  const completeUnfinishedCount = (() => {
    if (!completeSprintTarget) return 0;
    const list = issuesBySprint.get(Number(completeSprintTarget.id)) || [];
    const doneIds = new Set(
      statuses.filter((s) => s.category === "DONE").map((s) => s.id)
    );
    return list.filter((i) => !doneIds.has(i.status_id)).length;
  })();

  const sprintFormOpen = createSprintOpen || Boolean(editingSprint);

  return (
    <div>
      <style>{`
        .drag-grip { border-radius: 6px; transition: background .15s, color .15s; }
        .drag-grip:hover { background: #eff6ff; color: #3b82f6 !important; }
        .drag-grip:active { background: #dbeafe; color: #1d4ed8 !important; cursor: grabbing !important; }
      `}</style>

      <div className="card border-0 mb-3" style={{ borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
        <div className="card-header border-0">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h4 className="mb-1 d-flex align-items-center gap-2" style={{ whiteSpace: "nowrap" }}>
                <i className="ti ti-list-details" style={{ color: "#3b82f6", fontSize: 22, flexShrink: 0 }} />
                Backlog &amp; Sprints
              </h4>
              <span className="text-muted small">
                Plan sprints, then drag the <i className="ti ti-grip-vertical" /> handle to move issues between backlog and sprints.
              </span>

            </div>
            {canManageSprint && (
              <button
                type="button"
                className="btn btn-info btn-sm"
                onClick={() => setCreateSprintOpen(true)}
              >
                <i className="ti ti-plus me-1" />
                New Sprint
              </button>
            )}
          </div>
        </div>
      </div>

      {(sprintsLoading || issuesLoading) &&
        sprints.length === 0 &&
        (projectIssues || []).length === 0 ? (
        <div className="text-center py-4">
          <Spin />
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDrag(null)}
        >
          <SortableContext
            items={sprintRowIds}
            strategy={verticalListSortingStrategy}
          >
            {visibleSprints.length === 0 ? (
              <div className="card mb-3">
                <div className="card-body text-center py-4 text-muted small">
                  No sprints yet. Create one to start planning.
                </div>
              </div>
            ) : (
              visibleSprints.map((sprint) => (
                <SprintRow
                  key={sprint.id}
                  sprint={sprint}
                  issues={issuesBySprint.get(Number(sprint.id)) || []}
                  statuses={statuses}
                  projectKey={project.key}
                  isReorderable={canManageSprint && sprint.state === "PLANNED"}
                  canManageSprint={canManageSprint}
                  canDeleteSprint={canDeleteSprint}
                  onStart={handleStartSprint}
                  onComplete={handleCompleteSprintClick}
                  onEdit={setEditingSprint}
                  onDelete={handleDeleteSprint}
                />
              ))
            )}
          </SortableContext>

          <BacklogList
            issues={issuesBySprint.get("BACKLOG") || []}
            statuses={statuses}
            projectKey={project.key}
            canCreateIssue={canCreateIssue}
            onCreateIssueClick={() => setCreateIssueOpen(true)}
          />

          <DragOverlay dropAnimation={{ duration: 180 }}>
            {activeDrag?.kind === "ISSUE" && (
              <div
                className="card border-0 px-3 py-2"
                style={{
                  borderRadius: 8,
                  boxShadow: "0 14px 32px rgba(59,130,246,0.35)",
                  background: "#fff",
                  borderLeft: "3px solid #3b82f6",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  minWidth: 240,
                  maxWidth: 420,
                  cursor: "grabbing",
                }}
              >
                <span className="text-primary fw-semibold small" style={{ fontSize: 12 }}>
                  {project.key}-{activeDrag.issue.issue_key?.split("-")[1] || activeDrag.issue.id}
                </span>
                <span className="text-truncate">{activeDrag.issue.title}</span>
              </div>
            )}
            {activeDrag?.kind === "SPRINT" && (
              <div
                className="card border-0 px-3 py-2"
                style={{
                  borderRadius: 8,
                  boxShadow: "0 14px 32px rgba(59,130,246,0.35)",
                  background: "#fff",
                  borderLeft: "4px solid #3b82f6",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  minWidth: 280,
                  cursor: "grabbing",
                }}
              >
                <i className="ti ti-grip-vertical text-muted" />
                <span>{activeDrag.sprint.name}</span>
                <Tag color="default" style={{ margin: 0, fontSize: 10 }}>{activeDrag.sprint.state}</Tag>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      <CreateSprintInline
        open={sprintFormOpen}
        submitting={submittingSprint}
        onSubmit={editingSprint ? handleEditSprintSubmit : handleCreateSprint}
        onCancel={() => {
          setCreateSprintOpen(false);
          setEditingSprint(null);
        }}
      />

      <CompleteSprintModal
        open={Boolean(completeSprintTarget)}
        sprint={completeSprintTarget}
        unfinishedCount={completeUnfinishedCount}
        plannedSprints={plannedSprints}
        submitting={completing}
        onSubmit={handleCompleteSprintSubmit}
        onCancel={() => setCompleteSprintTarget(null)}
      />

      <IssueFormModal
        open={createIssueOpen}
        submitting={submittingIssue}
        onSubmit={handleCreateIssue}
        onCancel={() => setCreateIssueOpen(false)}
      />
    </div>
  );
};

export default BacklogPage;
